import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Clock, Edit, MapPin, Trash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppointmentForm } from '@/components/appointments/appointment-form';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CachedImage } from '@/components/ui/cached-image';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAppointments,
  useClaimAppointment,
  useDeleteAppointment,
  useUpdateAppointment,
} from '@/hooks/use-appointments';
import { getUserDisplayName, useUsers } from '@/hooks/use-users';
import { useAuth } from '@/providers/auth-provider';
import type { User } from '@/services/api';
import type { Appointment, CreateAppointmentRequest } from '@/types/appointment';

export const Route = createFileRoute('/needs-driver')({
  component: () => (
    <ProtectedRoute>
      <NeedsDriverPage />
    </ProtectedRoute>
  ),
});

function NeedsDriverPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [claimingAppointmentId, setClaimingAppointmentId] = useState<string | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [deletingAppointment, setDeletingAppointment] = useState<Appointment | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: appointmentsData, isLoading, error } = useAppointments();
  const claimAppointmentMutation = useClaimAppointment();
  const updateAppointmentMutation = useUpdateAppointment();
  const deleteAppointmentMutation = useDeleteAppointment();
  const { data: usersData } = useUsers();

  const users: Record<string, User> = usersData?.users || {};
  const allAppointments = appointmentsData?.appointments || [];
  const now = new Date();

  const unassignedAppointments = allAppointments
    .filter((appointment) => appointment.status === 'unassigned' && new Date(appointment.startTime) >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const handleClaimAppointment = async (appointmentId: string) => {
    try {
      setClaimingAppointmentId(appointmentId);
      await claimAppointmentMutation.mutateAsync(appointmentId);
    } catch (error) {
      console.error('Failed to claim appointment:', error);
    } finally {
      setClaimingAppointmentId(null);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment);
  };

  const handleUpdateAppointment = async (data: CreateAppointmentRequest) => {
    if (!editingAppointment) return;

    try {
      await updateAppointmentMutation.mutateAsync({
        id: editingAppointment.id,
        data,
      });
      setEditingAppointment(null);
    } catch (error) {
      console.error('Failed to update appointment:', error);
    }
  };

  const handleDeleteClick = (appointment: Appointment) => {
    setDeletingAppointment(appointment);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingAppointment) return;

    try {
      await deleteAppointmentMutation.mutateAsync(deletingAppointment.id);
      setShowDeleteDialog(false);
      setDeletingAppointment(null);
    } catch (error) {
      console.error('Failed to delete appointment:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('nav.needsDriver')}</h1>
        </div>
        <div className="text-center py-8">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('nav.needsDriver')}</h1>
        </div>
        <div className="text-center py-8 text-red-600">{t('appointments.errorLoading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{t('nav.needsDriver')}</h1>
        <Badge variant="destructive" className="text-lg px-4 py-2">
          {unassignedAppointments.length} {t('appointments.needingDrivers')}
        </Badge>
      </div>

      {unassignedAppointments.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="h-16 w-16 mx-auto mb-4 text-green-500 opacity-50" />
          <h3 className="text-lg font-medium text-green-800 mb-2">{t('appointments.allAppointmentsAssigned')}</h3>
          <p className="text-green-600">{t('appointments.noUnassignedDescription')}</p>
        </div>
      ) : (
        <div className="grid gap-4 md:gap-6">
          {unassignedAppointments.map((appointment) => (
            <Card key={appointment.id} className="border-red-200 bg-red-50">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-red-900">
                    {appointment.title}
                    <Badge variant="destructive">{t('appointments.needsDriver')}</Badge>
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditAppointment(appointment)}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="hidden sm:inline ml-1">{t('forms.edit')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(appointment)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash className="h-4 w-4" />
                      <span className="hidden sm:inline ml-1">{t('appointments.delete')}</span>
                    </Button>
                    <Button
                      onClick={() => handleClaimAppointment(appointment.id)}
                      disabled={claimingAppointmentId === appointment.id}
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 h-12 px-6 text-base md:h-10 md:px-4 md:text-sm shrink-0"
                    >
                      <span className="hidden sm:inline">
                        {claimingAppointmentId === appointment.id
                          ? t('appointments.claiming')
                          : t('appointments.claimThisAppointment')}
                      </span>
                      <span className="sm:hidden">
                        {claimingAppointmentId === appointment.id
                          ? t('appointments.claiming')
                          : t('appointments.claim')}
                      </span>
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-red-700">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {appointment.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {format(new Date(appointment.startTime), 'EEEE, MMM d, yyyy - HH:mm')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {appointment.duration} {t('time.minutes')}
                  </span>
                </div>
                {appointment.description && (
                  <div className="text-sm text-red-800 mt-3 p-3 bg-red-100 rounded border border-red-200">
                    {appointment.description}
                  </div>
                )}
                {appointment.createdBy && (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-red-100 rounded border border-red-200">
                    {users[appointment.createdBy]?.image && (
                      <CachedImage
                        src={users[appointment.createdBy].image}
                        alt={getUserDisplayName(users[appointment.createdBy], appointment.createdBy, session?.user?.id)}
                        className="w-5 h-5 rounded-full"
                      />
                    )}
                    <span className="text-sm font-medium text-red-800">
                      {t('appointments.createdBy')}{' '}
                      {getUserDisplayName(users[appointment.createdBy], appointment.createdBy, session?.user?.id)}
                    </span>
                  </div>
                )}
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {editingAppointment && (
        <AppointmentForm
          open={true}
          onOpenChange={(open) => !open && setEditingAppointment(null)}
          onSubmit={handleUpdateAppointment}
          appointment={editingAppointment}
          isLoading={updateAppointmentMutation.isPending}
          mode="edit"
        />
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('appointments.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('appointments.deleteConfirmation')}
              {deletingAppointment && <div className="mt-2 font-medium">"{deletingAppointment.title}"</div>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('forms.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleteAppointmentMutation.isPending}>
              {deleteAppointmentMutation.isPending ? t('appointments.deleting') : t('appointments.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
